// Sprint 66 — POS Invoice History
//
// Lists every POS-channel order with invoice info, filters, and the
// production-ready action buttons (View / Print Again / Download PDF /
// Return / Exchange). Return + Exchange are STUB launchers — full flow
// is a future sprint per spec.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, money, errMsg } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { Search, Eye, Printer, FilePlus, RotateCcw, RefreshCw, Repeat, Receipt } from "lucide-react";

const ALLOWED = ["SUPER_ADMIN", "STORE_ADMIN", "STORE_OWNER", "STORE_MANAGER", "CASHIER"];

const fmtDT = (iso) => {
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

export default function POSInvoices() {
  const { user, booting } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [invoiceById, setInvoiceById] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/orders?limit=200&sort=-created_at");
      const posOnly = (data.items || []).filter((o) => o.channel === "pos");
      setOrders(posOnly);
      // Lazy-hydrate invoice numbers (existing endpoint, no new API)
      const missing = posOnly.filter((o) => !invoiceById[o.id]);
      Promise.all(missing.slice(0, 40).map((o) =>
        api.get(`/invoices/by-order/${o.id}`).then((r) => ({ oid: o.id, inv: r.data })).catch(() => null),
      )).then((rows) => {
        setInvoiceById((prev) => {
          const next = { ...prev };
          rows.filter(Boolean).forEach((r) => { next[r.oid] = r.inv; });
          return next;
        });
      });
    } catch (e) { toast.error(errMsg(e, "Could not load invoices")); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return orders;
    return orders.filter((o) => {
      const inv = invoiceById[o.id]?.invoice_number || "";
      return (
        (o.order_number || "").toLowerCase().includes(t) ||
        inv.toLowerCase().includes(t) ||
        (o.address?.name || "").toLowerCase().includes(t) ||
        (o.address?.phone || "").toLowerCase().includes(t)
      );
    });
  }, [orders, q, invoiceById]);

  const printReceipt = (order, size = "80mm") => {
    api.get(`/invoices/by-order/${order.id}`).catch(() => ({ data: null })).then((r) => {
      const invoice = r?.data || invoiceById[order.id];
      const w = window.open("", "_blank", "width=460,height=760");
      if (!w) return toast.error("Popup blocked — allow pop-ups");
      const isA4 = size === "a4";
      w.document.write(buildHTML(order, invoice, isA4));
      w.document.close();
    });
  };

  if (booting) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;
  if (!user || !ALLOWED.includes(user.role)) return <Navigate to="/admin/dashboard" replace />;

  return (
    <div className="p-6" data-testid="pos-invoices-page">
      <PageHeader
        title={<span className="inline-flex items-center gap-2"><Receipt className="w-5 h-5 text-primary" />POS Invoice History</span>}
        subtitle="Every completed POS bill · reprint, download PDF, or start a return/exchange"
        actions={
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search invoice, order, customer…"
                className="pl-9 h-9 w-72"
                data-testid="pos-inv-search"
              />
            </div>
            <Button variant="outline" onClick={load} className="h-9"><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
            <Button asChild className="h-9"><Link to="/admin/pos">Back to POS</Link></Button>
          </div>
        }
      />

      <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-left text-[10px] font-bold uppercase tracking-widest text-slate-500">
              <th className="p-3">Invoice</th>
              <th className="p-3">Order</th>
              <th className="p-3">Customer</th>
              <th className="p-3">Cashier / Store</th>
              <th className="p-3">Date &amp; time</th>
              <th className="p-3">Payment</th>
              <th className="p-3 text-right">Total</th>
              <th className="p-3">Status</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td colSpan={9} className="p-3"><Skeleton className="h-6 w-full" /></td>
                </tr>
              ))
            ) : filtered.length === 0 ? (
              <tr><td colSpan={9} className="p-10 text-center text-slate-400">No POS invoices yet.</td></tr>
            ) : filtered.map((o) => {
              const inv = invoiceById[o.id];
              const cashier = (o.notes || "").split("cashier ")[1]?.split(" ")[0] || "—";
              return (
                <tr key={o.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`pos-inv-row-${o.id}`}>
                  <td className="p-3 font-semibold text-slate-900">{inv?.invoice_number || "—"}</td>
                  <td className="p-3 text-slate-700">{o.order_number}</td>
                  <td className="p-3">
                    <div className="text-slate-800">{o.address?.name || "Walk-in"}</div>
                    <div className="text-[11px] text-slate-500">{o.address?.phone || ""}</div>
                  </td>
                  <td className="p-3 text-slate-700 text-[12px]">{cashier}</td>
                  <td className="p-3 text-slate-600 text-[12px]">{fmtDT(o.created_at)}</td>
                  <td className="p-3 uppercase text-[11px] font-semibold tracking-widest text-slate-600">{o.payment?.method || "—"}</td>
                  <td className="p-3 text-right font-semibold tabular-nums">{money(o.pricing?.total || 0)}</td>
                  <td className="p-3">
                    <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-emerald-100 text-emerald-800">{o.status}</span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-1 justify-end">
                      <Button variant="ghost" size="sm" title="View" onClick={() => window.open(`/admin/orders/${o.id}`, "_blank")} data-testid={`pos-inv-view-${o.id}`}><Eye className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" title="Print Again" onClick={() => printReceipt(o, "80mm")} data-testid={`pos-inv-print-${o.id}`}><Printer className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" title="Download PDF" onClick={() => printReceipt(o, "a4")} data-testid={`pos-inv-pdf-${o.id}`}><FilePlus className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" title="Return (coming soon)" onClick={() => toast.info("Return flow launches in the next sprint")} data-testid={`pos-inv-return-${o.id}`}><RotateCcw className="w-4 h-4 text-amber-600" /></Button>
                      <Button variant="ghost" size="sm" title="Exchange (coming soon)" onClick={() => toast.info("Exchange flow launches in the next sprint")} data-testid={`pos-inv-exchange-${o.id}`}><Repeat className="w-4 h-4 text-sky-600" /></Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-2 text-[11px] text-slate-500">Showing {filtered.length} POS invoice{filtered.length === 1 ? "" : "s"} · scoped to your store (multi-store isolated).</div>
    </div>
  );
}

// Inline HTML builder — imported flavor of the one in AdminPOS.jsx so this page
// prints correctly even if AdminPOS isn't mounted. Small duplication is
// intentional (kept isolated to Invoice History) to avoid a shared-lib refactor.
function buildHTML(order, invoice, isA4) {
  const p = order.pricing || {}, addr = order.address || {};
  const rows = (order.items || []).map((it) => `<tr><td>${it.name}<br><span style="font-size:9px;color:#666">${it.qty} × ₹${Math.round(it.unit_price)}</span></td><td style="text-align:right">₹${Math.round(it.qty * it.unit_price)}</td></tr>`).join("");
  const total = Math.round(p.total || 0);
  const disc  = Math.round(p.discount || p.extra_discount || 0);
  const method = (order.payment?.method || "cash").toUpperCase();
  const dt = new Date(order.created_at || Date.now()).toLocaleString("en-IN");
  if (isA4) {
    return `<!doctype html><html><head><meta charset="utf-8"><title>Invoice ${invoice?.invoice_number || order.order_number}</title>
<style>@page{size:A4;margin:14mm}*{font-family:'Poppins',ui-sans-serif,system-ui}body{color:#111;font-size:12px}.brandbar{border-bottom:3px solid #DC1E1E;padding-bottom:10px;margin-bottom:16px;display:flex;justify-content:space-between}.brandbar h1{margin:0;color:#DC1E1E}table{width:100%;border-collapse:collapse}th,td{border-bottom:1px solid #eee;padding:8px}th{background:#fef2f2;color:#DC1E1E;text-align:left;font-size:10px;text-transform:uppercase}.grand{background:#DC1E1E;color:#fff;padding:8px 12px;border-radius:6px;display:flex;justify-content:space-between;font-weight:700;font-size:14px;margin-top:10px}</style></head><body>
<div class="brandbar"><div><h1>BAMKO</h1><div>Invoice ${invoice?.invoice_number || order.order_number}</div><div style="color:#555">${dt}</div></div><div style="text-align:right"><div>${addr.name || "Walk-in"}</div><div style="color:#555">${addr.phone || ""}</div><div style="margin-top:6px;padding:2px 10px;background:#dcfce7;color:#166534;border-radius:999px;font-size:10px;letter-spacing:.6px;display:inline-block">${method}</div></div></div>
<table><thead><tr><th>Item</th><th style="text-align:right">Amount</th></tr></thead><tbody>${rows}</tbody></table>
${disc ? `<div style="text-align:right;color:#166534;margin-top:8px">Discount − ₹${disc}</div>` : ""}
<div class="grand"><span>Grand Total</span><span>₹${total}</span></div>
<script>window.onload=()=>window.print();</script></body></html>`;
  }
  return `<!doctype html><html><head><meta charset="utf-8"><title>Receipt</title><style>@page{margin:0;size:72mm auto}body{font-family:monospace;font-size:11px;padding:4mm}h1{font-size:13px;text-align:center;margin:0 0 4px}.r{text-align:right}table{width:100%;border-collapse:collapse}hr{border:0;border-top:1px dashed #999;margin:6px 0}</style></head><body>
<h1>BAMKO</h1><div style="text-align:center">${invoice?.invoice_number || order.order_number}</div><div style="text-align:center;color:#555">${dt}</div>
<hr><table>${rows}</table><hr>
${disc ? `<div class="r">Discount − ₹${disc}</div>` : ""}
<div class="r" style="font-weight:bold;font-size:13px">TOTAL ₹${total}</div>
<hr><div style="text-align:center">Thank you!</div>
<script>window.onload=()=>window.print();</script></body></html>`;
}
